import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Button, EmptyState, ResponsiveDataView, Select, type DataColumn } from "@akuna/ui";
import { useHasPermissionSync } from "@/hooks/usePermissions";
import { useCatalogGroups, useCommissionReport } from "@/hooks/useCommissions";
import { useTeamMembers } from "@/hooks/useTeam";
import { downloadCsv } from "@/lib/financial/csv";
import { formatCurrency, formatDate, formatPercent } from "@/lib/format/br";
import { groupKey, type GroupKind } from "@/lib/commissions/groups";
import type { CommissionReportRow } from "@/lib/commissions/types";
import { periodPresets } from "@/pages/financial/helpers";
import { RangePicker, type PeriodState } from "@/pages/financial/PeriodPicker";
import { CommissionAssignmentsSection } from "./CommissionAssignmentsSection";
import { summarize, toCsvRows } from "./commissionReport";

// Radix Select items cannot have an empty value, so "all" uses a sentinel.
const ALL = "__all__";

export function TeamCommissionsTab() {
  const { t, i18n } = useTranslation("commissions");
  const today = new Date().toISOString().slice(0, 10);
  const { hasPermission: canViewAll } = useHasPermissionSync("commissions", "view");
  const { hasPermission: canViewCatalog } = useHasPermissionSync("catalog", "view");
  const { data: members = [] } = useTeamMembers(canViewAll);
  const { data: groups = [] } = useCatalogGroups(canViewCatalog);
  const [period, setPeriod] = useState<PeriodState>(() => {
    const p = periodPresets(today).find((x) => x.key === "thisMonth")!;
    return { from: p.from, to: p.to, presetKey: p.key };
  });
  const [professionalId, setProfessionalId] = useState("");
  const [group, setGroup] = useState("");
  const [kind, groupId] = group ? (group.split(":") as [GroupKind, string]) : [null, null];

  const { data: rows = [], isLoading } = useCommissionReport({
    from: period.from, to: period.to, professionalId: professionalId || null, kind, groupId,
  });
  const summary = useMemo(() => summarize(rows), [rows]);

  const columns: DataColumn<CommissionReportRow>[] = [
    { id: "date", header: t("report.columns.date"), cell: (r) => formatDate(r.occurred_at, i18n.language) },
    { id: "sale", header: t("report.columns.sale"), cell: (r) => r.sale_number, priority: "hidden-mobile" },
    { id: "pro", header: t("report.columns.professional"), cell: (r) => r.professional_name },
    {
      id: "item", header: t("report.columns.item"), priority: "primary",
      cell: (r) => (
        <span className="flex items-center gap-2">
          {r.item_name}
          {r.entry_kind === "reversal" && <Badge variant="danger">{t("report.reversal")}</Badge>}
        </span>
      ),
    },
    { id: "group", header: t("report.columns.group"), cell: (r) => r.group_name || t("catalog.models.noGroup") },
    { id: "base", header: t("report.columns.base"), cell: (r) => <span className="tabular-nums">{formatCurrency(r.base_amount, i18n.language)}</span> },
    { id: "pct", header: t("report.columns.percent"), cell: (r) => formatPercent(r.percent, i18n.language) },
    { id: "amount", header: t("report.columns.amount"), cell: (r) => <span className="font-semibold tabular-nums">{formatCurrency(r.amount, i18n.language)}</span> },
  ];

  return (
    <div className="flex flex-col gap-6">
      {canViewAll && <CommissionAssignmentsSection />}
      {!canViewAll && <h2 className="text-lg font-semibold">{t("report.mine")}</h2>}
      <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-end">
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium">{t("report.period")}</span>
          <RangePicker value={period} onChange={setPeriod} today={today} />
        </div>
        {canViewAll && (
          <Select
            className="md:w-56"
            label={t("report.professional")}
            value={professionalId || ALL}
            onValueChange={(v) => setProfessionalId(v === ALL ? "" : v)}
            options={[{ value: ALL, label: t("report.allProfessionals") }, ...members.map((m) => ({ value: m.user_id, label: m.full_name }))]}
          />
        )}
        <Select
          className="md:w-56"
          label={t("report.group")}
          value={group || ALL}
          onValueChange={(v) => setGroup(v === ALL ? "" : v)}
          options={[{ value: ALL, label: t("report.allGroups") }, ...groups.map((g) => ({ value: groupKey(g.kind, g.id), label: `${g.name} · ${t(`kinds.${g.kind}`)}` }))]}
        />
        <Button
          variant="outline"
          className="md:ml-auto"
          disabled={rows.length === 0}
          onClick={() => downloadCsv(`${t("report.csvFile")}_${period.from}_${period.to}.csv`, toCsvRows(rows, t, i18n.language))}
        >
          {t("report.exportCsv")}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="flex flex-col gap-1.5 rounded-card border border-border bg-secondary p-5">
          <span className="text-sm text-muted-foreground">{t("report.total")}</span>
          <span className="text-2xl font-bold tabular-nums" data-testid="commission-total">{formatCurrency(summary.total, i18n.language)}</span>
        </div>
        {summary.people.map((p) => (
          <div key={p.id} className="flex flex-col gap-2 rounded-card border border-border bg-card p-5">
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-semibold">{p.name}</span>
              <span className="font-bold tabular-nums">{formatCurrency(p.total, i18n.language)}</span>
            </div>
            <ul className="flex flex-col gap-1 text-sm text-muted-foreground">
              {p.groups.map((g) => (
                <li key={g.name} className="flex justify-between gap-2"><span>{g.name || t("catalog.models.noGroup")}</span><span className="tabular-nums">{formatCurrency(g.amount, i18n.language)}</span></li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <section aria-label={t("report.items")}>
        <ResponsiveDataView
          items={rows}
          columns={columns}
          loading={isLoading}
          getItemId={(r) => r.id}
          getItemLabel={(r) => r.item_name}
          emptyContent={<EmptyState title={t("report.empty")} />}
        />
      </section>
    </div>
  );
}
